/**
 * MessageOpened — Prototype 01b (State 2)
 * Route: /prototypes/message-opened
 *
 * Katrina has chosen to open Damon's message. That choice is the beginning
 * of the demonstration.
 *
 * The Home doesn't disappear. It quiets. It moves out of focus but stays
 * on the surface as the room she is still in. The message expands to the
 * center — still the same card, just larger and given room to speak.
 *
 * Damon speaks first. Not Toto. Not Sonmi. A human she already trusts.
 * His message is short by intent — one sentence introduces the person he
 * wants her to meet. Nothing more.
 *
 * Presence has NOT yet awakened. It is still the small resting mark in
 * the corner it was on the previous screen. The interface has not
 * transformed. What's changed is only that Katrina is now paying
 * attention to one thing.
 */
import React from "react";
import PrototypeShell from "./PrototypeShell";
import PresenceForm from "./PresenceForm";

export default function MessageOpened() {
  const notes = {
    communicates:
      "Katrina's attention has moved to Damon's message because she chose to open it. The surrounding Home has quieted; the message is centered. Damon speaks briefly. Nothing else about the interface has transformed. The demonstration is beginning, but the interface is not yet announcing anything.",
    placeholder: [
      "The prerecorded video is a placeholder rectangle labelled 'Damon Mitchell · 00:00 / 00:00'. Final: a real short recording, ~15–25 seconds.",
      "The transcription text under the video is the intended message copy — will be replaced by voice in final delivery.",
      "The Home ghost behind is a static low-opacity snapshot. In final delivery, this is the actual State 1 view faded to a shared background.",
      "The 'End of message' state is not shown here — that's what State 3 (Handoff) shows.",
    ],
    decisions: [
      "Home currently sits at ~15% opacity. Alternatives: (a) fully hidden — 0% — but that severs the sense of place, (b) ~35% — but then it competes. Currently: 15%, quiet-but-present.",
      "Should Damon's message auto-play on state entry, or wait for Katrina to press play? Currently: prototype shows a paused state — Katrina initiates. In final: probably auto-play, since she already chose to open it.",
      "Should the message body appear as (a) video with transcript below (current), (b) video only, or (c) text-only with narration audio? Currently: (a) so the design accommodates all possibilities without requiring the recording.",
      "Should there be a way to close the message and return to Home? Currently: no. Opening the message is a one-way threshold.",
    ],
    assumptions: [
      "Katrina wants to see Damon's face. Text-only would feel evasive. If the final recording is voice-only, we redesign this frame.",
      "Presence remains in its resting corner mark. It has NOT awakened yet. Awakening belongs to State 3.",
      "The message plays inline on the Home. It does not open a new page or a modal-with-close. There is no 'X' — this is a room she has entered, not a dialog she can dismiss.",
    ],
  };

  return (
    <PrototypeShell title="Prototype 01 · Message Opened (state 2 — Damon speaks)" notes={notes}>
      <section className="relative min-h-[calc(100vh-6rem)]" data-testid="message-opened">
        {/* Home ghost — the *shape* of her Home still visible, but the greeting
             text is intentionally omitted here to avoid crossing the message.
             The card silhouettes carry the sense-of-place. */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.12]"
          aria-hidden="true"
          data-testid="home-ghost"
        >
          <div className="max-w-5xl mx-auto px-8 lg:px-12 pt-52 space-y-3">
            <div className="rounded-2xl bg-white subtle-border px-6 py-5 h-[92px]" />
            <div className="rounded-2xl bg-white subtle-border px-6 py-5 h-[92px]" />
            <div className="rounded-2xl bg-white subtle-border px-6 py-5 h-[92px]" />
            <div className="rounded-2xl bg-white subtle-border px-6 py-5 h-[92px]" />
          </div>
        </div>

        {/* Presence — same resting mark as State 1, still in the corner */}
        <div className="absolute top-4 right-6 lg:right-12">
          <PresenceForm size={44} tone="neutral" testId="opened-presence-rest" />
        </div>

        {/* Message — centered, expanded, but visually the same card material */}
        <div className="relative max-w-2xl mx-auto pt-20 pb-16 px-8" data-testid="opened-message">
          <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-3">Message from Damon Mitchell</div>

          {/* Prerecorded message placeholder */}
          <div
            className="rounded-2xl bg-white subtle-border overflow-hidden"
            data-testid="opened-video-placeholder"
          >
            <div className="aspect-video bg-stone-100 flex items-center justify-center text-stone-400 text-xs italic">
              [prerecorded video · Damon Mitchell · 00:00 / 00:00]
            </div>
            <div className="px-6 py-5">
              <div className="text-[13px] text-stone-500 mb-3">Damon · General Manager, Plantation · this morning</div>
              <p className="text-[17px] text-stone-800 leading-relaxed font-serif-display" style={{ fontWeight: 400 }}>
                Good morning, Katrina.
                <br />
                Rather than explain what we've been building, I'd like to introduce you to someone.
              </p>
            </div>
          </div>

          <div className="mt-5 text-[11px] text-stone-400 italic">
            (prototype — video is placeholder; audio not yet recorded)
          </div>
        </div>
      </section>
    </PrototypeShell>
  );
}
